/**
 * SommelAI Enhanced AI Service Integration
 *
 * Comprehensive AI service orchestration layer that coordinates between:
 * - Multiple AI models (Claude, GPT-4, Gemini)
 * - External beverage APIs (Vivino, Untappd, Wine.com, etc.)
 * - Data enhancement pipelines
 * - Real-time intelligence gathering
 *
 * Phase 1 Implementation: AI Integration Foundation
 */

import {
  generateComprehensiveWineInfo,
  generateWineClassificationInfo,
  generateTastingNotes,
  generateRatings,
  callAnthropicAI,
  callGeminiAI,
} from "./aiService.js";

import { aggregateExternalData } from "./externalApiIntegration.js";

// AI Model Configuration
export const AI_SERVICE_CONFIG = {
  models: {
    primary: "claude-3-5-sonnet-20241022",
    fallback: "gemini-pro",
    specialized: {
      wine: "claude-3-5-sonnet-20241022",
      beer: "gemini-pro",
      spirits: "claude-3-5-sonnet-20241022",
      cocktails: "gemini-pro",
      pairing: "claude-3-5-sonnet-20241022",
    },
  },
  rateLimits: {
    claude: { rpm: 50, tpm: 40000 },
    gemini: { rpm: 60, tpm: 32000 },
    gpt4: { rpm: 10, tpm: 30000 },
  },
  retryConfig: {
    maxRetries: 3,
    backoffMs: 1000,
    maxBackoffMs: 10000,
  },
};

// External API Configuration
export const EXTERNAL_API_CONFIG = {
  vivino: {
    enabled: true,
    priority: 1,
    categories: ["wine"],
    rateLimit: 100, // requests per hour
  },
  untappd: {
    enabled: true,
    priority: 1,
    categories: ["beer"],
    rateLimit: 100,
  },
  wineCom: {
    enabled: true,
    priority: 2,
    categories: ["wine"],
    rateLimit: 1000,
  },
  cocktailDB: {
    enabled: true,
    priority: 1,
    categories: ["cocktails", "spirits"],
    rateLimit: 1000,
  },
};

/**
 * Main AI Enhancement Orchestrator
 * Coordinates between AI models and external APIs to create comprehensive beverage intelligence
 */
export class AIServiceIntegration {
  constructor() {
    this.initialized = false;
    this.apiKeys = this.loadApiKeys();
    this.requestQueue = [];
    this.processing = false;
  }

  /**
   * Load and validate API keys
   */
  loadApiKeys() {
    const keys = {
      anthropic: process.env.REACT_APP_ANTHROPIC_API_KEY,
      gemini: process.env.REACT_APP_GEMINI_API_KEY,
      openai: process.env.REACT_APP_OPENAI_API_KEY,
      vivino: process.env.REACT_APP_VIVINO_API_KEY,
      untappd: process.env.REACT_APP_UNTAPPD_CLIENT_ID,
      wineCom: process.env.REACT_APP_WINE_COM_API_KEY,
    };

    // Validate critical keys
    if (!keys.anthropic && !keys.gemini && !keys.openai) {
      console.warn(
        "⚠️ No AI service API keys found. AI features will be limited."
      );
    }

    return keys;
  }

  /**
   * Initialize AI service integration
   */
  async initialize() {
    try {
      console.log("🚀 Initializing SommelAI Service Integration...");

      // Test AI service connectivity
      await this.testAIConnectivity();

      // Test external API connectivity
      await this.testExternalAPIs();

      this.initialized = true;
      console.log("✅ AI Service Integration initialized successfully");

      return {
        success: true,
        services: {
          ai: this.getAvailableAIServices(),
          external: this.getAvailableExternalAPIs(),
        },
      };
    } catch (error) {
      console.error("❌ Failed to initialize AI Service Integration:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Enhanced beverage processing - Phase 1 core function
   */
  async enhanceBeverage(beverage, options = {}) {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      console.log(
        `🔄 Enhancing ${beverage.category || "beverage"}: ${beverage.name}`
      );

      const enhancementPlan = this.createEnhancementPlan(beverage, options);
      const results = await this.executeEnhancementPlan(enhancementPlan);

      return this.synthesizeResults(beverage, results);
    } catch (error) {
      console.error("❌ Beverage enhancement failed:", error);
      return this.createFallbackEnhancement(beverage, error);
    }
  }

  /**
   * Create enhancement plan based on beverage type and available services
   */
  createEnhancementPlan(beverage, options) {
    const category =
      beverage.category?.toLowerCase() || this.detectCategory(beverage);

    const plan = {
      beverage,
      category,
      tasks: [],
      priority: options.priority || "standard",
      timeout: options.timeout || 30000,
    };

    // Add AI enhancement tasks
    plan.tasks.push({
      type: "ai_enhancement",
      model:
        AI_SERVICE_CONFIG.models.specialized[category] ||
        AI_SERVICE_CONFIG.models.primary,
      functions: this.getAIFunctionsForCategory(category),
    });

    // Add external API tasks
    const externalAPIs = this.getExternalAPIsForCategory(category);
    if (externalAPIs.length > 0) {
      plan.tasks.push({
        type: "external_data",
        apis: externalAPIs,
        parallel: true,
      });
    }

    // Add data synthesis task
    plan.tasks.push({
      type: "synthesis",
      model: AI_SERVICE_CONFIG.models.primary,
    });

    return plan;
  }

  /**
   * Execute enhancement plan with parallel processing where possible
   */
  async executeEnhancementPlan(plan) {
    const results = {};

    for (const task of plan.tasks) {
      try {
        switch (task.type) {
          case "ai_enhancement":
            results.aiData = await this.executeAIEnhancement(
              plan.beverage,
              task
            );
            break;
          case "external_data":
            results.externalData = await this.executeExternalDataGathering(
              plan.beverage,
              task
            );
            break;
          case "synthesis":
            results.synthesized = await this.executeSynthesis(
              plan.beverage,
              results,
              task
            );
            break;
        }
      } catch (error) {
        console.warn(`⚠️ Task ${task.type} failed:`, error.message);
        results[`${task.type}_error`] = error.message;
      }
    }

    return results;
  }

  /**
   * Execute AI enhancement using configured models
   */
  async executeAIEnhancement(beverage, task) {
    const results = {};

    // Execute AI functions in parallel where possible
    const promises = task.functions.map(async (func) => {
      try {
        switch (func) {
          case "comprehensive_info":
            return [
              "comprehensive",
              await generateComprehensiveWineInfo(beverage),
            ];
          case "classification":
            return [
              "classification",
              await generateWineClassificationInfo(beverage),
            ];
          case "tasting_notes":
            return ["tastingNotes", await generateTastingNotes(beverage)];
          case "ratings":
            return ["ratings", await generateRatings(beverage)];
          case "beer_analysis":
            return ["beerAnalysis", await this.generateBeerAnalysis(beverage)];
          case "spirits_analysis":
            return [
              "spiritsAnalysis",
              await this.generateSpiritsAnalysis(beverage),
            ];
          case "cocktail_optimization":
            return [
              "cocktailOptimization",
              await this.generateCocktailOptimization(beverage),
            ];
          case "pairing_analysis":
            return [
              "pairingAnalysis",
              await this.generatePairingAnalysis(beverage),
            ];
          case "style_analysis":
            // For beer style analysis, use the beer analysis function
            return ["styleAnalysis", await this.generateBeerAnalysis(beverage)];
          case "distillery_info":
            // For spirits distillery info, use the spirits analysis function
            return [
              "distilleryInfo",
              await this.generateSpiritsAnalysis(beverage),
            ];
          case "recipe_optimization":
            // For cocktail recipe optimization
            return [
              "recipeOptimization",
              await this.generateCocktailOptimization(beverage),
            ];
          case "ingredient_analysis":
            // For cocktail ingredient analysis
            return [
              "ingredientAnalysis",
              await this.generateCocktailOptimization(beverage),
            ];
          default:
            console.warn(`⚠️ Unknown AI function: ${func}`);
            return [func, null];
        }
      } catch (error) {
        console.warn(`⚠️ AI function ${func} failed:`, error.message);
        return [func, null];
      }
    });

    const functionResults = await Promise.allSettled(promises);

    functionResults.forEach((result) => {
      if (result.status === "fulfilled" && result.value[1]) {
        results[result.value[0]] = result.value[1];
      }
    });

    return results;
  }

  /**
   * Execute external data gathering from multiple APIs
   */
  async executeExternalDataGathering(beverage, task) {
    const results = {};

    if (task.parallel) {
      // Execute API calls in parallel
      const promises = task.apis.map(async (api) => {
        try {
          const data = await aggregateExternalData(
            beverage,
            beverage.category,
            { source: api }
          );
          return [api, data];
        } catch (error) {
          console.warn(`⚠️ External API ${api} failed:`, error.message);
          return [api, null];
        }
      });

      const apiResults = await Promise.allSettled(promises);

      apiResults.forEach((result) => {
        if (result.status === "fulfilled" && result.value[1]) {
          results[result.value[0]] = result.value[1];
        }
      });
    }

    return results;
  }

  /**
   * Synthesize all collected data into final enhanced beverage object
   */
  async executeSynthesis(beverage, collectedData, task) {
    // This will use AI to intelligently combine all data sources
    const synthesisPrompt = this.createSynthesisPrompt(beverage, collectedData);

    try {
      // Use primary AI model for synthesis
      const synthesized = await this.callAIModel(task.model, synthesisPrompt);
      return synthesized;
    } catch (error) {
      console.warn(
        "⚠️ Synthesis failed, using rule-based combination:",
        error.message
      );
      return this.ruleBasisSynthesis(beverage, collectedData);
    }
  }

  /**
   * Get AI functions for specific beverage category
   */
  getAIFunctionsForCategory(category) {
    const baseFunctions = ["comprehensive_info", "tasting_notes", "ratings"];

    switch (category) {
      case "wine":
        return [...baseFunctions, "classification", "pairing_analysis"];
      case "beer":
        return ["beer_analysis", "style_analysis", "pairing_analysis"];
      case "spirits":
        return ["spirits_analysis", "distillery_info", "pairing_analysis"];
      case "cocktails":
        return [
          "cocktail_optimization",
          "recipe_optimization",
          "ingredient_analysis",
        ];
      default:
        // For unknown categories, use wine functions as fallback
        return baseFunctions;
    }
  }

  /**
   * Get external APIs for specific beverage category
   */
  getExternalAPIsForCategory(category) {
    const apis = [];

    Object.entries(EXTERNAL_API_CONFIG).forEach(([name, config]) => {
      if (config.enabled && config.categories.includes(category)) {
        apis.push(name);
      }
    });

    return apis.sort(
      (a, b) =>
        EXTERNAL_API_CONFIG[a].priority - EXTERNAL_API_CONFIG[b].priority
    );
  }

  /**
   * Detect beverage category from name and description
   */
  detectCategory(beverage) {
    const text = `${beverage.name} ${beverage.description || ""}`.toLowerCase();

    if (
      text.includes("wine") ||
      text.includes("cabernet") ||
      text.includes("chardonnay") ||
      text.includes("pinot")
    ) {
      return "wine";
    }
    if (
      text.includes("beer") ||
      text.includes("ale") ||
      text.includes("lager") ||
      text.includes("stout")
    ) {
      return "beer";
    }
    if (
      text.includes("whiskey") ||
      text.includes("vodka") ||
      text.includes("gin") ||
      text.includes("rum")
    ) {
      return "spirits";
    }
    if (
      text.includes("cocktail") ||
      text.includes("martini") ||
      text.includes("mojito")
    ) {
      return "cocktails";
    }

    return "wine"; // Default fallback
  }

  /**
   * Create synthesis prompt for AI model
   */
  createSynthesisPrompt(beverage, data) {
    return `
      Synthesize comprehensive beverage information for: ${beverage.name}
      
      Available data sources:
      ${JSON.stringify(data, null, 2)}
      
      Create a unified, accurate, and comprehensive beverage profile that:
      1. Combines all reliable data sources
      2. Resolves any conflicts between sources
      3. Fills gaps with intelligent inference
      4. Maintains accuracy and professionalism
      
      Return as JSON with the structure defined in the beverage schema.
    `;
  }

  /**
   * Rule-based synthesis fallback when AI synthesis fails
   */
  ruleBasisSynthesis(beverage, data) {
    const synthesized = {
      ...beverage,
      enhanced: true,
      enhancementTimestamp: new Date().toISOString(),
      dataSources: Object.keys(data),
    };

    // Combine AI data
    if (data.aiData) {
      Object.assign(synthesized, data.aiData);
    }

    // Merge external data with priority
    if (data.externalData) {
      Object.entries(data.externalData).forEach(([source, sourceData]) => {
        if (sourceData && typeof sourceData === "object") {
          synthesized.externalSources = synthesized.externalSources || {};
          synthesized.externalSources[source] = sourceData;
        }
      });
    }

    return synthesized;
  }

  /**
   * Test AI service connectivity
   */
  async testAIConnectivity() {
    const tests = [];

    if (this.apiKeys.anthropic) {
      tests.push(this.testAnthropicConnection());
    }
    if (this.apiKeys.gemini) {
      tests.push(this.testGeminiConnection());
    }

    if (tests.length === 0) {
      throw new Error("No AI service API keys configured");
    }

    const results = await Promise.allSettled(tests);
    const successful = results.filter((r) => r.status === "fulfilled").length;

    console.log(
      `✅ AI Connectivity: ${successful}/${tests.length} services available`
    );
  }

  /**
   * Test external API connectivity
   */
  async testExternalAPIs() {
    // This will be implemented to test each configured external API
    console.log("🔗 Testing external API connectivity...");

    // For now, just log the configured APIs
    const enabled = Object.entries(EXTERNAL_API_CONFIG)
      .filter(([, config]) => config.enabled)
      .map(([name]) => name);

    console.log(`📡 External APIs configured: ${enabled.join(", ")}`);
  }

  /**
   * Get available AI services
   */
  getAvailableAIServices() {
    return {
      claude: !!this.apiKeys.anthropic,
      gemini: !!this.apiKeys.gemini,
      openai: !!this.apiKeys.openai,
    };
  }

  /**
   * Get available external APIs
   */
  getAvailableExternalAPIs() {
    return Object.fromEntries(
      Object.entries(EXTERNAL_API_CONFIG).map(([name, config]) => [
        name,
        config.enabled && !!this.apiKeys[name],
      ])
    );
  }

  /**
   * Call AI model with automatic fallback
   */
  async callAIModel(model, prompt) {
    try {
      // Route to appropriate AI service based on model
      if (model.includes("claude")) {
        if (!this.apiKeys.anthropic) {
          throw new Error("Anthropic API key not configured");
        }
        return await callAnthropicAI(prompt, model);
      } else if (model.includes("gemini")) {
        if (!this.apiKeys.gemini) {
          throw new Error("Gemini API key not configured");
        }
        return await callGeminiAI(prompt, model);
      } else if (model.includes("gpt")) {
        // OpenAI implementation would go here when available
        throw new Error("OpenAI integration not yet implemented");
      } else {
        // Try fallback model
        const fallbackModel = AI_SERVICE_CONFIG.models.fallback;
        if (fallbackModel !== model) {
          console.warn(
            `⚠️ Unknown model ${model}, falling back to ${fallbackModel}`
          );
          return await this.callAIModel(fallbackModel, prompt);
        } else {
          throw new Error(`Unsupported AI model: ${model}`);
        }
      }
    } catch (error) {
      console.error(`❌ AI model call failed for ${model}:`, error.message);

      // Try fallback if available and different from current model
      const fallbackModel = AI_SERVICE_CONFIG.models.fallback;
      if (fallbackModel !== model && this.shouldRetryWithFallback(error)) {
        console.log(`🔄 Retrying with fallback model: ${fallbackModel}`);
        try {
          return await this.callAIModel(fallbackModel, prompt);
        } catch (fallbackError) {
          console.error(
            `❌ Fallback model also failed:`,
            fallbackError.message
          );
          throw new Error(`All AI models failed: ${error.message}`);
        }
      }

      throw error;
    }
  }

  /**
   * Determine if we should retry with fallback model
   */
  shouldRetryWithFallback(error) {
    const retryableErrors = [
      "rate limit",
      "quota",
      "timeout",
      "network",
      "service unavailable",
    ];

    const errorMessage = error.message.toLowerCase();
    return retryableErrors.some((retryableError) =>
      errorMessage.includes(retryableError)
    );
  }

  /**
   * Test Anthropic connection
   */
  async testAnthropicConnection() {
    if (!this.apiKeys.anthropic) {
      throw new Error("Anthropic API key not configured");
    }

    try {
      // Simple test call with minimal token usage
      const testResult = await callAnthropicAI(
        'Respond with exactly: {"status": "ok", "service": "anthropic"}',
        "claude-3-5-sonnet-20241022"
      );

      if (testResult && testResult.status === "ok") {
        return "Anthropic connection successful";
      } else {
        throw new Error("Unexpected response from Anthropic API");
      }
    } catch (error) {
      throw new Error(`Anthropic connection failed: ${error.message}`);
    }
  }

  /**
   * Test Gemini connection
   */
  async testGeminiConnection() {
    if (!this.apiKeys.gemini) {
      throw new Error("Gemini API key not configured");
    }

    try {
      // Simple test call with minimal token usage
      const testResult = await callGeminiAI(
        'Respond with exactly: {"status": "ok", "service": "gemini"}',
        "gemini-pro"
      );

      if (testResult && testResult.status === "ok") {
        return "Gemini connection successful";
      } else {
        throw new Error("Unexpected response from Gemini API");
      }
    } catch (error) {
      throw new Error(`Gemini connection failed: ${error.message}`);
    }
  }

  /**
   * Create fallback enhancement when AI services fail
   */
  createFallbackEnhancement(beverage, error) {
    return {
      ...beverage,
      enhanced: false,
      enhancementError: error.message,
      enhancementTimestamp: new Date().toISOString(),
      fallbackData: {
        message: "AI enhancement temporarily unavailable",
        basicInfo: this.generateBasicInfo(beverage),
      },
    };
  }

  /**
   * Generate basic info without AI when services are unavailable
   */
  generateBasicInfo(beverage) {
    return {
      category: this.detectCategory(beverage),
      displayName: beverage.name,
      basicDescription: beverage.description || "No description available",
      status: "Basic information only - AI enhancement pending",
    };
  }

  /**
   * Enhanced beer analysis using AI
   */
  async generateBeerAnalysis(beverage) {
    const prompt = `Analyze this beer and provide comprehensive information:
    
    Beer: ${beverage.name}
    Description: ${beverage.description || "No description provided"}
    Type: ${beverage.type || "Unknown type"}
    
    Generate analysis as JSON:
    {
      "beerStyle": {
        "primary": "primary beer style classification",
        "secondary": "secondary style if applicable",
        "bjcpCategory": "BJCP style guide category if applicable"
      },
      "tastingNotes": {
        "appearance": "color, clarity, head description",
        "aroma": "hop, malt, yeast, and other aromatics",
        "flavor": "taste profile including hop/malt balance",
        "mouthfeel": "body, carbonation, finish description"
      },
      "technicalSpecs": {
        "abv": "estimated alcohol by volume",
        "ibu": "estimated international bitterness units",
        "srm": "estimated standard reference method color",
        "servingTemp": "optimal serving temperature range"
      },
      "brewery": {
        "estimatedStyle": "brewery's typical style focus",
        "region": "brewing region characteristics",
        "techniques": "likely brewing techniques used"
      },
      "foodPairings": ["specific food pairing recommendations"],
      "professionalNotes": "expert tasting notes and recommendations"
    }`;

    return await this.callAIModel(
      AI_SERVICE_CONFIG.models.specialized.beer,
      prompt
    );
  }

  /**
   * Enhanced spirits analysis using AI
   */
  async generateSpiritsAnalysis(beverage) {
    const prompt = `Analyze this spirit and provide comprehensive information:
    
    Spirit: ${beverage.name}
    Description: ${beverage.description || "No description provided"}
    Type: ${beverage.type || "Unknown type"}
    Region: ${beverage.region || "Unknown region"}
    
    Generate analysis as JSON:
    {
      "spiritType": {
        "category": "whiskey/vodka/gin/rum/brandy/tequila/etc",
        "subcategory": "specific subcategory (bourbon, single malt, etc)",
        "ageStatement": "age information if determinable",
        "proof": "estimated alcohol proof"
      },
      "tastingProfile": {
        "nose": "aroma characteristics",
        "palate": "flavor profile and complexity",
        "finish": "length and character of finish",
        "overall": "overall tasting impression"
      },
      "production": {
        "mashbill": "estimated grain bill if applicable",
        "distillation": "distillation method insights",
        "maturation": "aging and barrel information",
        "filtration": "filtration process if known"
      },
      "distillery": {
        "region": "distillery region characteristics",
        "style": "house style and philosophy",
        "heritage": "historical significance",
        "expertise": "known specialties"
      },
      "servingRecommendations": {
        "neat": "neat serving notes",
        "cocktails": ["recommended cocktail applications"],
        "temperature": "optimal serving temperature",
        "glassware": "recommended glassware"
      },
      "investmentNotes": "collectibility and investment potential",
      "expertRating": "estimated expert rating range"
    }`;

    return await this.callAIModel(
      AI_SERVICE_CONFIG.models.specialized.spirits,
      prompt
    );
  }

  /**
   * Enhanced cocktail optimization using AI
   */
  async generateCocktailOptimization(beverage) {
    const prompt = `Analyze this cocktail and provide optimization recommendations:
    
    Cocktail: ${beverage.name}
    Description: ${beverage.description || "No description provided"}
    Ingredients: ${beverage.ingredients || "Not specified"}
    
    Generate optimization as JSON:
    {
      "cocktailProfile": {
        "style": "cocktail family/style classification",
        "complexity": "simple/moderate/complex preparation",
        "flavor": "primary flavor profile description",
        "strength": "estimated alcohol content level"
      },
      "recipeOptimization": {
        "ingredients": [
          {
            "ingredient": "ingredient name",
            "amount": "recommended amount",
            "quality": "quality recommendations",
            "substitutes": ["possible substitutions"]
          }
        ],
        "technique": "preparation technique recommendations",
        "garnish": "optimal garnish suggestions",
        "glassware": "recommended glassware"
      },
      "variations": [
        {
          "name": "variation name",
          "changes": "what changes from original",
          "occasion": "when to serve this variation"
        }
      ],
      "pairingContext": {
        "occasion": ["suitable occasions"],
        "season": "seasonal appropriateness",
        "foodPairings": ["food pairing suggestions"],
        "atmosphere": "recommended atmosphere/setting"
      },
      "presentation": {
        "visual": "visual presentation recommendations",
        "serving": "service style recommendations",
        "temperature": "optimal serving temperature"
      },
      "expertTips": "professional bartender insights and tips"
    }`;

    return await this.callAIModel(
      AI_SERVICE_CONFIG.models.specialized.cocktails,
      prompt
    );
  }

  /**
   * Enhanced pairing analysis using AI
   */
  async generatePairingAnalysis(beverage, menuItems = []) {
    const menuContext =
      menuItems.length > 0
        ? `Restaurant menu items: ${menuItems
            .map((item) => item.name)
            .join(", ")}`
        : "No specific menu provided";

    const prompt = `Generate intelligent food pairing recommendations:
    
    Beverage: ${beverage.name}
    Category: ${beverage.category || "unknown"}
    Description: ${beverage.description || "No description"}
    ${menuContext}
    
    Generate pairings as JSON:
    {
      "primaryPairings": [
        {
          "food": "specific food/dish name",
          "confidence": "high/medium/low pairing confidence",
          "reason": "why this pairing works",
          "preparation": "recommended preparation style"
        }
      ],
      "cuisineAffinities": [
        {
          "cuisine": "cuisine type",
          "compatibility": "excellent/good/fair",
          "examples": ["specific dishes from this cuisine"]
        }
      ],
      "flavorBridges": {
        "complementary": ["flavors that complement"],
        "contrasting": ["flavors that contrast nicely"],
        "avoid": ["flavors to avoid"]
      },
      "menuIntegration": [
        {
          "course": "appetizer/main/dessert",
          "recommendations": ["specific menu integration suggestions"],
          "timing": "when in the meal to serve"
        }
      ],
      "occasionPairings": {
        "casual": ["casual dining suggestions"],
        "formal": ["fine dining suggestions"],
        "celebrations": ["special occasion pairings"]
      },
      "seasonalPairings": {
        "spring": ["spring-appropriate pairings"],
        "summer": ["summer-appropriate pairings"],
        "fall": ["fall-appropriate pairings"],
        "winter": ["winter-appropriate pairings"]
      }
    }`;

    return await this.callAIModel(
      AI_SERVICE_CONFIG.models.specialized.pairing,
      prompt
    );
  }
}

// Export singleton instance
export const aiServiceIntegration = new AIServiceIntegration();

// Export convenience functions
export const enhanceBeverage = (beverage, options) =>
  aiServiceIntegration.enhanceBeverage(beverage, options);

export const initializeAIServices = () => aiServiceIntegration.initialize();

export default aiServiceIntegration;
